

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { API_BASE_URL } from "@/config/api";
import { isOwnerRole } from "@/utils/roles";
import styles from "./Netexposure.module.css";

// Helper to reorder "vs" parts so Draw is always in the middle
const formatEventName = (name) => {
  if (!name) return name;
  const parts = name.split(" vs ");
  if (parts.length !== 3) return name;
  const drawIndex = parts.findIndex(
    (p) => p.trim().toLowerCase() === "the draw" || p.trim().toLowerCase() === "draw"
  );
  if (drawIndex === -1 || drawIndex === 1) return name;
  const draw = parts.splice(drawIndex, 1)[0];
  parts.splice(1, 0, draw);
  return parts.join(" vs ");
};

// Helper to map runners to Standard Columns: 1 (Home), X (Draw), 2 (Away)
// const getStandardRunners = (market) => {
//   const runners = market.runners || [];

//   // 1. Identify Draw
//   const drawRunner = runners.find(r => r.toLowerCase() === 'the draw' || r.toLowerCase() === 'draw' || r.toLowerCase() === 'x');

//   // If not identified or not distinct, fallback
//   if (!drawRunner) return runners;

//   // 2. Identify Home and Away from Market Name.
//   // Handles "Team A v Team B" and "Team A v Team B v The Draw"
//   const parts = (market.marketName || '').split(/\s+v\s+|\s+vs\s+/i);
//   if (parts.length < 2) return runners; // Can't parse name properly

//   // Assume standard format: Part 0 is Home, Part 1 is Away
//   const homeName = parts[0].trim();
//   const awayName = parts[1].trim();

//   // 3. Find Home & Away runners
//   const homeRunner = runners.find(r => r !== drawRunner && (r.includes(homeName) || homeName.includes(r)));
//   const awayRunner = runners.find(r => r !== drawRunner && (r.includes(awayName) || awayName.includes(r)));

//   if (homeRunner && awayRunner) {
//     // Return in order matching columns: 1 (Home), X (Draw), 2 (Away)
//     return [homeRunner, drawRunner, awayRunner];
//   }

//   return runners;
// };

const getStandardRunners = (market) => {
  const runners = market.runners || [];
  const name = market.marketName || '';

  if (!runners.length) return runners;

  // Split by vs or v
  const parts = name.split(/\s+v\s+|\s+vs\s+/i);

  if (parts.length < 2) return runners;

  const home = parts[0].trim();
  const away = parts[1].trim();

  let homeRunner = null;
  let awayRunner = null;
  let drawRunner = null;

  runners.forEach(r => {
    const lower = r.toLowerCase();

    if (lower.includes('draw') || lower === 'x') {
      drawRunner = r;
      return;
    }

    if (!homeRunner && (r.includes(home) || home.includes(r))) {
      homeRunner = r;
      return;
    }

    if (!awayRunner && (r.includes(away) || away.includes(r))) {
      awayRunner = r;
      return;
    }
  });

  const ordered = [];

  if (homeRunner) ordered.push(homeRunner);
  if (drawRunner) ordered.push(drawRunner);
  if (awayRunner) ordered.push(awayRunner);

  return ordered.length ? ordered : runners;
};

export default function NetExposure() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exposureData, setExposureData] = useState([]);
  const [meta, setMeta] = useState({ totalEvents: 0, totalExposure: 0 });

  const [timer, setTimer] = useState(10);

  // Helper to get auth token
  const getAuthToken = () => {
    const activeRole = localStorage.getItem('activeRole');
    if (isOwnerRole(activeRole)) {
      return localStorage.getItem('ownerToken');
    } else if (activeRole === 'staff') {
      return localStorage.getItem('staffToken');
    }
    return localStorage.getItem('token');
  };

  // Fetch net exposure data from API
  const fetchNetExposure = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const token = getAuthToken();
      // ✅ Fetching from NEW Sports Endpoint
      const response = await axios.get(
        `${API_BASE_URL}/lords/net-exposure/sports`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        }
      );

      if (response.data && response.data.success) {
        // Transform API response to match component structure
        const transformedData = response.data.data.map((event, index) => ({
          id: event.id || index + 1,
          eventId: event.eventId,
          eventName: event.eventName,
          eventDate: event.eventDate ? new Date(event.eventDate).toLocaleString() : '',
          category: event.category || 'sports',
          markets: event.markets.map((market, mIndex) => ({
            id: market.id || mIndex + 1,
            marketName: market.marketName,
            gameType: market.gameType, // ✅ Map gameType
            matchId: market.matchId,
            selections: market.selections || {},
            runners: market.runners || [],
            stake: market.stake || 0,
            pl: market.pl || 0,
          })),
        }));

        setExposureData(transformedData);
        setMeta(response.data.meta || { totalEvents: 0, totalExposure: 0 });
      } else {
        setExposureData([]);
      }
    } catch (error) {
      console.error("Error fetching net exposure data:", error);
      setExposureData([]);
    } finally {
      if (showLoading) setLoading(false);
      // setTimer(10); // Removed to allow custom timer logic
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchNetExposure();
  }, []);

  // Timer Countdown Effect
  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          fetchNetExposure(false); // Silent update
          return 8;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setTimer(8);
    await fetchNetExposure(false);
  };

  const formatexposure = (value) => {
    const numericValue = Number(value) || 0;
    if (numericValue < 0) {
      return Math.abs(numericValue).toFixed(2);
    }
    return numericValue.toFixed(2);
  };

  return (
    <Layout>
      <div className={styles.container}>
        {/* Header Section */}
        <div className={styles.headerSection}>
          <div className={styles.headerContent}>
            <div className={styles.titleContainer}>
              <div className={styles.pageTitle}>
                Net Exposure
              </div>
              <div className={styles.separator}></div>
            </div>
          </div>
        </div>


        {/* Table Section */}
        <div className={styles.tableSection}>
          <div className={styles.tableContainer}>
            {/* Action Bar - Updated Layout */}
            <div className={styles.actionBar}>
              {/* Row 1: Timer and Refresh */}
              <div className={styles.timerRow}>
                <span className={styles.timer}>{timer}</span>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className={styles.refreshButton}
                >
                  {loading ? "Refreshing..." : "Refresh"}
                </button>
              </div>
              {/* Row 2: Static Header */}
              <div className={styles.staticHeaderRow}>
                {/* Columns matching table cells */}
                <div className={styles.headerEvent}></div> {/* Empty for Event/Market Name alignment */}
                <div className={styles.headerStake}>Stake</div>
                <div className={styles.headerRunner}>1</div>
                <div className={styles.headerRunner}>X</div>
                <div className={styles.headerRunner}>2</div>
                <div className={styles.headerStake}>Stake</div>
                <div className={styles.headerRunner}>P/L</div>
                {/* If more runners, they flow? But static header is fixed. 3 is standard for Match Odds */}
              </div>
            </div>

            {loading ? (
              <div className={styles.loadingState}>Loading...</div>
            ) : exposureData.length === 0 ? (
              <div className={styles.emptyState}>No exposure data found</div>
            ) : (
              (() => {
                // Helper to determine if market is standard (Match Odds/Bookmaker) or Fancy
                const isStandardMarket = (gameType) => {
                  const typeStr = (gameType || '').toLowerCase();
                  return typeStr.includes('match_odds') || typeStr.includes('bookmaker') || typeStr.includes('match odds');
                };

                // Split events into separate cards based on market types
                return exposureData.flatMap((event) => {
                  const standardMarkets = event.markets.filter(m => isStandardMarket(m.gameType));
                  const fancyMarkets = event.markets.filter(m => !isStandardMarket(m.gameType));

                  const cards = [];

                  // Default Card for Standard Markets (Match Odds / Bookmaker)
                  if (standardMarkets.length > 0) {
                    cards.push(
                      <div key={`${event.id}-standard`} className={styles.eventCard}>
                        {/* Event Header Row */}
                        <div className={styles.eventHeader}>
                          <span>{formatEventName(event.eventName)}</span>
                          <span className={styles.eventDate}>({event.eventDate})</span>
                        </div>

                        {/* Markets Table */}
                        <table className={styles.marketTable}>
                          <tbody>
                            {standardMarkets.map((market) => (
                              <tr key={market.id} className={styles.marketRow}>
                                {/* Market Name / Game Type */}
                                <td className={styles.marketNameCell}>
                                  <div className={styles.marketContent}>
                                    {market.gameType ? market.gameType.replace(/_/g, ' ').toUpperCase() : market.marketName}
                                    <a
                                      href={`/gamedetailnew/4/${event?.eventId}`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/game-details/4/${event?.eventId}/1`);
                                      }}
                                    >
                                      <img
                                        src="https://d3kb8xz339pq18.cloudfront.net/v12/static/backend/images/icons/exposure.png"
                                        className={styles.exposureIcon}
                                        alt="exposure"
                                      />
                                    </a>
                                  </div>
                                </td>

                                {/* Stake Column */}
                                <td className={styles.stakeCell}>
                                  {market.runners && market.runners.length > 0
                                    ? ((market.selections[getStandardRunners(market)[0]] || 0) * -1).toFixed(2)
                                    : '0.00'}
                                </td>

                                {/* Dynamic Runner Columns */}
                                {market.runners && market.runners.length > 0 ? (
                                  getStandardRunners(market).map((runnerName, idx) => {
                                    const pl = market.selections[runnerName] || 0;
                                    return (
                                      <td key={idx} className={styles.runnerCell}>
                                        <div className={styles.runnerContent}>
                                          <div className={`${styles.plValue} ${pl < 0 ? styles.textGreen : styles.textRed}`}>
                                            {formatexposure(pl)}
                                          </div>
                                        </div>
                                      </td>
                                    );
                                  })
                                ) : (
                                  <td className={styles.noRunners}>No runners</td>
                                )}
                                <td className={styles.emptyFiller}></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  }

                  // Fancy Card for Fancy/Session Markets
                  if (fancyMarkets.length > 0) {
                    cards.push(
                      <div key={`${event.id}-fancy`} className={styles.eventCard}>
                        {/* Event Header Row */}
                        <div className={styles.eventHeader}>
                          <span>{formatEventName(event.eventName)}</span>
                          <span className={styles.eventDate}>({event.eventDate})</span>
                        </div>

                        {/* Sub-Header Row with Event Name and Fancy Tag */}
                        <div className={styles.fancySubHeader}>
                          <span>{formatEventName(event.eventName)}</span>
                          <span className={styles.fancyTag}>fancy</span>
                        </div>

                        {/* Markets Table - Right Floated Layout */}
                        <table className={styles.marketTable}>
                          <tbody>
                            {fancyMarkets.map((market) => {
                              // For Fancy markets, iterate through each runner to show selections
                              return market.runners && market.runners.map((runnerName, idx) => {
                                const exposureValue = market.selections[runnerName] || 0;
                                return (
                                  <tr key={`${market.id}-${idx}`} className={styles.marketRow}>
                                    {/* Game Type - Spans first 5 columns, right-aligned */}
                                    <td colSpan={5} className={styles.fancyMarketNameCell}>
                                      <div className={styles.fancyMarketContent}>
                                        {market.gameType || market.marketName}
                                        <a
                                          href={`/gamedetailnew/4/${event?.eventId}`}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            navigate(`/game-details/4/${event?.eventId}/1`);
                                          }}
                                        >
                                          <img
                                            src="https://d3kb8xz339pq18.cloudfront.net/v12/static/backend/images/icons/exposure.png"
                                            className={styles.exposureIcon}
                                            alt="exposure"
                                          />
                                        </a>
                                      </div>
                                    </td>

                                    {/* Stake - Right Side */}
                                    <td className={styles.stakeCell}>
                                      {typeof market.stake === 'number' ? market.stake.toFixed(2) : '0.00'}
                                    </td>

                                    {/* Exposure Value from Selections - Right Side */}
                                    <td className={styles.runnerCell}>
                                      <div className={styles.runnerContent}>
                                        <div className={`${styles.plValue} ${exposureValue < 0 ? styles.textGreen : styles.textRed}`}>
                                          {formatexposure(exposureValue)}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              });
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  }

                  return cards;
                });
              })()
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
